// Generated by tutorials/record.cjs from tutorials/articles/h60-printers-and-printing.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h60 --text-only` (wording) or
// `node tutorials/record.cjs h60` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'printers-and-printing',
  catalogId: 'h60',
  title: 'Printers and printing problems',
  summary: 'How printing works in Dietech Online: reports you print from your browser, tray tickets that print by themselves on your kitchen\'s printers, where each is set up, and what to check when tickets do not print.',
  audience: 'Dietary managers and anyone who sets up or looks after the kitchen\'s printers',
  area: 'setup',
  learn: [
    'Print or save a report from your browser, and find the saved file',
    'How tray tickets reach your kitchen\'s printers through the print agent',
    'Choose which printer each kind of ticket goes to',
    'Set when tray tickets print by themselves',
    'What to check when tray tickets did not print',
  ],
  keywords: [
    'printer',
    'printers',
    'printing',
    'print',
    'print agent',
    'dietech print agent',
    'not printing',
    'did not print',
    'tickets not printing',
    'tray tickets not printing',
    'offline',
    'printer offline',
    'routing',
    'routing rules',
    'print schedule',
    'print schedules',
    'automatic printing',
    'auto print',
    'downloads',
    'download folder',
    'where is my file',
    'saved report',
    'reprint',
    'print jobs',
    'kitchen printer',
  ],
  steps: [
    {
      id: 'viewer-print',
      part: 'Print a report yourself',
      heading: 'Print from your browser',
      text: 'A report you open yourself prints through your browser, on any printer your computer can use. Open the report from Reports or the Print Queue, then select the printer button at the top.\n\nYour browser\'s print window opens. Pick the printer there and print.',
      media: {
        image: {
          src: '/help/printers-and-printing/viewer-print.webp',
          width: 1280,
          height: 760,
        },
        alt: 'A Diet Order report open on screen, with the Print button at the top of the viewer marked.',
      },
    },
    {
      id: 'viewer-save',
      part: 'Print a report yourself',
      heading: 'Save a copy, and find it',
      text: 'To keep a copy, select the download button and choose PDF, Excel or Word. Your browser saves the file, named after the report, usually in your Downloads folder, or asks you where to put it.\n\nCannot find it? Open your browser\'s list of downloads (Ctrl and J in Chrome and Edge).',
      media: {
        image: {
          src: '/help/printers-and-printing/viewer-save.webp',
          width: 1280,
          height: 760,
        },
        clip: {
          src: '/help/printers-and-printing/viewer-save.mp4',
          width: 1280,
          height: 760,
          durationSec: 2.9,
        },
        alt: 'The download menu open in the report viewer, offering PDF, Excel and Word.',
      },
    },
    {
      id: 'agent',
      part: 'Tickets that print by themselves',
      heading: 'The print agent',
      text: 'Tray tickets that print without anyone opening them, such as Print Now in Selective Touch, go straight to your kitchen\'s printers. That works through the Dietech Print Agent, a small program on a computer at your facility that can reach those printers. Dietech sends each ticket to it, and it prints the ticket.\n\nTo see it, select Setup, then Facility Settings, then Print Agent. Status Online means it checked in within the last couple of minutes.',
      media: {
        image: {
          src: '/help/printers-and-printing/agent.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Registered Print Agents on the Print Agent tab: Kitchen Office on KITCHEN-PC-01, Online, last seen 40 seconds ago, with 4 printers, and Dining Room on DINING-PC-02, Offline, last seen 3 hours ago.',
      },
    },
    {
      id: 'printers',
      part: 'Tickets that print by themselves',
      heading: 'Your printers',
      text: 'Setup, then Printers, lists the printers Dietech can send to. Friendly Name is the name your staff see. Printer Name must match the printer\'s name on the print agent\'s computer exactly, or nothing prints on it.\n\nType says what the printer gets: a Menu Ticket printer gets whole tray tickets, and an Always Served printer only the always served items.',
      media: {
        image: {
          src: '/help/printers-and-printing/printers.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Printers page listing Kitchen Tickets (Menu Ticket), Beverage Station (Always Served) and Office Laser (Scheduled Reports), all enabled.',
      },
    },
    {
      id: 'printer-find',
      part: 'Tickets that print by themselves',
      heading: 'Add a printer',
      text: 'To add one, select New. Find Printer lists the printers the print agent found on its computer, so you can pick the exact name instead of typing it. Give it a Friendly Name and a Printer Type, then save.',
      media: {
        image: {
          src: '/help/printers-and-printing/printer-find.webp',
          width: 944,
          height: 604,
        },
        clip: {
          src: '/help/printers-and-printing/printer-find.mp4',
          width: 944,
          height: 604,
          durationSec: 3.7,
        },
        alt: 'The Find Printer window listing the printers on the print agent computers, such as Kitchen Ticket Printer and Beverage Printer, each with its print agent and whether that agent is Online.',
      },
    },
    {
      id: 'routing',
      part: 'Tickets that print by themselves',
      heading: 'Choose where each ticket goes',
      text: 'Back on Facility Settings, Print Agent, Printer Routing Rules says where each kind of printing goes: the Report Type (such as Tray Ticket or Production Tally), the Production type (all of it, or only the hot line, cold line, desserts or beverages), the unit, and the printer.\n\nSelect Add Rule to add one, or the pencil to change one.',
      media: {
        image: {
          src: '/help/printers-and-printing/routing.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Printer Routing Rules: Tray Ticket to Kitchen Tickets, Tray Ticket for Beverage to Beverage Station, and Production Tally to Office Laser, all enabled.',
      },
    },
    {
      id: 'st-printers',
      part: 'Tickets that print by themselves',
      heading: 'Printers for Print Now',
      text: 'In Selective Touch, select Printers at the top to choose which printers Print Now uses for you. Each person picks their own. Connected means the print agent can reach the printer.',
      media: {
        image: {
          src: '/help/printers-and-printing/st-printers.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/printers-and-printing/st-printers.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.3,
        },
        alt: 'Selective Touch\'s Printers screen: Kitchen Tickets switched on and Beverage Station switched off, both Connected.',
      },
    },
    {
      id: 'schedules',
      part: 'Tickets that print by themselves',
      heading: 'When tickets print by themselves',
      text: 'Setup, then Print Schedules, sets when each meal\'s tray tickets print by themselves. Default Times are the meal\'s ready times, and Print Before Ready Time is how many minutes before each one the tickets print.\n\nThey print as the Auto Print User. With no user picked, nothing prints automatically.\n\nAutomatic printing also has to be switched on for your facility. If your tickets do not print by themselves at these times, ask Dietech support.',
      media: {
        image: {
          src: '/help/printers-and-printing/schedules.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Print Schedules: Breakfast at 7:30 AM, Lunch at 11:30 AM and 12:00 PM, and Dinner at 4:30 PM and 5:00 PM, each printing 30 minutes before, with Dana Morgan as the Auto Print User.',
      },
    },
    {
      id: 'trouble-agent',
      part: 'My tray tickets didn\'t print',
      heading: 'Check the print agent',
      text: 'Open Setup, Facility Settings, Print Agent. If the Status is Offline or Warning, or Last Seen is more than a few minutes ago, the print agent\'s computer is probably turned off, asleep or not connected.\n\nTurn the computer on and check it is connected to your network. The print agent starts with the computer and prints the tickets that were waiting.',
      media: {
        image: {
          src: '/help/printers-and-printing/trouble-agent.webp',
          width: 1232,
          height: 210,
        },
        alt: 'The Dining Room print agent on DINING-PC-02 marked Offline, last seen 3 hours ago.',
      },
    },
    {
      id: 'trouble-jobs',
      part: 'My tray tickets didn\'t print',
      heading: 'Check what was sent',
      text: 'Lower on the same tab, Recent Print Jobs lists what was sent to your printers in the last day. Pending is waiting for the print agent, Completed printed, and Failed did not print: Message says why.\n\nA printer that was not found is usually turned off, out of paper, unplugged or renamed. Fix the printer, then print the tickets again.',
      media: {
        image: {
          src: '/help/printers-and-printing/trouble-jobs.webp',
          width: 1232,
          height: 296,
        },
        alt: 'Recent Print Jobs: a Pending tray ticket for Dining Room Printer, a Completed one, a Failed one for Beverage Printer with the message that the printer was not found, and a Completed Production Tally.',
      },
    },
    {
      id: 'trouble-queue',
      part: 'My tray tickets didn\'t print',
      heading: 'Check the Print Queue',
      text: 'In the Print Queue (Reports, then Print Queue), check that the tickets were made. Pending or Generating means Dietech is still making them, Error means they could not be made, and No Data means there was nothing to print. See Favorites and the print queue.',
      media: {
        image: {
          src: '/help/printers-and-printing/trouble-queue.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Print Queue, with the Tray Tickets for lunch marked.',
      },
    },
    {
      id: 'trouble-reprint',
      part: 'My tray tickets didn\'t print',
      heading: 'Print them again',
      text: 'To print what is missing, select the tickets in the Print Queue, then Print: they open on screen and print from your browser. To print one resident\'s ticket, use the Tray Ticket Visualizer (see Previewing and printing tray tickets).\n\nStill not printing? Call Dietech support.',
      media: {
        image: {
          src: '/help/printers-and-printing/trouble-reprint.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Tray Tickets row selected in the Print Queue, with the Print button marked.',
      },
    },
  ],
  tips: [
    'Keep the print agent\'s computer turned on and set it not to go to sleep, so tickets print at any hour.',
    'Give printers names your staff will know, such as Kitchen Tickets or Beverage Station.',
    'A report you print from your browser does not use the print agent, so you can still print it while the print agent is off.',
  ],
  notes: [
    'Printed in the Print Queue means someone opened the report and selected Print. It does not show what the print agent printed: Recent Print Jobs on the Print Agent tab does.',
    'Changing the print agent, the routing rules or the print schedules needs Facility security level 2 or higher.',
    'To set up the print agent on a new or replaced computer, call Dietech support.',
    'Print Schedules saves your times even while automatic printing is not switched on for your facility. Until it is, print tray tickets from Print Tray Tickets.',
  ],
  related: ['tray-tickets', 'favorites-and-print-queue', 'running-a-report', 'selective-touch'],
  recorded: '2026-10-28',
};

export default article;
